import { useMemo } from 'react';
import { Stack, Grid, Card, CardHeader, CardContent, Box, TextField, Button, Tooltip, Typography } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { CrudFilters, getDefaultFilter } from '@refinedev/core';
import { List, useDataGrid, EditButton, BooleanField, DateField, DeleteButton } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { Controller } from 'react-hook-form';

const TagSetsList = () => {
  const { dataGridProps, filters, search } = useDataGrid({
    onSearch: (params: { name: string }) => {
      const filters: CrudFilters = [];

      filters.push({
        field: 'name',
        operator: 'eq',
        value: params.name
      });

      return filters;
    },

    filters: {
      initial: [
        {
          field: 'name',
          operator: 'eq',
          value: ''
        }
      ]
    }
  });

  const { control, handleSubmit } = useForm({
    defaultValues: {
      name: getDefaultFilter('name', filters, 'eq') || ''
    }
  });

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        width: 200,
        flex: 2,
        renderCell: params => {
          return (
            <Tooltip title={params.row.name}>
              <Typography variant="body1" noWrap>
                {params.row.name}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        field: 'tags',
        headerName: 'Tags',
        flex: 1,
        sortable: false,
        renderCell: ({ value }) => value.length
      },
      {
        field: 'created',
        headerName: 'Created At',
        renderCell: ({ value }) => <DateField format="LLL" value={value} />,
        flex: 1,
        sortable: false
      },
      {
        field: 'modified',
        headerName: 'Updated At',
        renderCell: ({ value }) => <DateField format="LLL" value={value} />,
        flex: 1,
        sortable: false
      },
      {
        field: 'visible',
        headerName: 'Visible',
        renderCell: ({ value }) => <BooleanField value={value} valueLabelFalse="Not visible" valueLabelTrue="Visible" />
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 250,
        sortable: false,
        renderCell: params => {
          return (
            <Stack direction="row" spacing={1}>
              <EditButton hideText recordItemId={params.row.id} />
              <DeleteButton
                hideText
                recordItemId={params.row.id}
                confirmTitle={'Do you want to delete TagSet: "' + params.row.name + '" ?'}
              />
            </Stack>
          );
        }
      }
    ],
    []
  );

  // Remove sorting until https://github.com/refinedev/refine/issues/7099 is fixed
  const { sortingMode, sortModel, onSortModelChange, ...restDataGridProps } = dataGridProps;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={3}>
        <Card>
          <CardHeader title="Filters" />
          <CardContent>
            <Box
              component="form"
              autoComplete="off"
              sx={{
                display: 'flex',
                flexDirection: 'column'
              }}
              onSubmit={handleSubmit(search)}
            >
              <Controller
                name="name"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    label="Tagset"
                    placeholder="Search by Tagset name"
                    margin="normal"
                    fullWidth
                    autoFocus
                  />
                )}
              />
              <Button type="submit" variant="contained">
                Apply
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} lg={9}>
        <List canCreate>
          <DataGridPro {...restDataGridProps} filterModel={undefined} columns={columns} disableColumnMenu pagination />
        </List>
      </Grid>
    </Grid>
  );
};

export default TagSetsList;
